import { useMemo } from "react";
import { useAuth } from "react-oidc-context";

import type { Role } from "shell/platform/types/types";

import { featureFlags } from "shell/platform/config/featureFlags";
import {
  FULL_ACCESS_ROLES,
  INTERNAL_EMAIL_DOMAIN,
  INTERNAL_ROLES,
  PORTAL_ROLES,
  ROLES_CLAIM_KEY,
} from "shell/platform/constants/constants";
import { useDecodedTokens } from "shell/platform/hooks/useDecodedTokens";

export const useMockedPortalRoles = () => {
  return {
    isLoading: false,
    hasNoRoles: false,
    hasInvalidInternalRoleDomain: false,
    hasRole: (roles: Array<Role>) =>
      PORTAL_ROLES.some(role => roles.includes(role)),
    hasPortalReadAccess: true,
    hasPlatformSwitchAccess: true,
    hasReadAccess: true,
    hasPanFinderAccess: true,
    hasWebhookAccess: true,
    allowedActions: {
      cardLoadAndUnload: true,
      editCardConfigurations: true,
      changeCardStatus: true,
      activateCard: true,
      extendThreddExpiryDate: true,
      EditCardHolderDetails: true,
      removeAuth: true,
      balanceTransfer: true,
      balanceAdjustment: true,
      pinCvvServices: true,
    },
  };
};

export const useOIDCPortalRoles = () => {
  const auth = useAuth();
  const { idToken } = useDecodedTokens();

  const userRoles = useMemo(() => {
    if (!idToken) return [];
    try {
      if (!idToken[ROLES_CLAIM_KEY]) return [];
      return idToken[ROLES_CLAIM_KEY];
    } catch (_error) {
      // Silently return empty array if token decoding fails; caller handles empty roles
      return [];
    }
  }, [idToken]);

  const hasRole = (roles: Array<Role>) =>
    userRoles.some(role => roles.includes(role));

  const hasInternalRole = hasRole(INTERNAL_ROLES);
  const userEmail = idToken?.email ?? "";
  const internalRoleEmail = import.meta.env.VITE_INTERNAL_ROLE_EMAIL;
  const hasInvalidInternalRoleDomain =
    hasInternalRole &&
    !auth?.isLoading &&
    !userEmail.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN) &&
    userEmail.toLowerCase() !== internalRoleEmail?.toLowerCase();

  const hasPortalReadAccess = hasRole([
    ...INTERNAL_ROLES,
    ...FULL_ACCESS_ROLES,
    "Read Only",
    "Card Operations Specialist",
    "Card Configuration Manager",
    "Card Balance Manager",
  ]);

  const hasPanFinderAccess =
    featureFlags.enablePanFinder && hasRole(["Sensitive Information Manager"]);

  const hasPlatformSwitchAccess = hasRole(["Internal", "Internal Read Only"]);

  const hasWebhookAccess =
    featureFlags.enableWebhookAccess && hasRole(["Internal"]);

  return {
    isLoading: auth?.isLoading,
    hasNoRoles: userRoles.length === 0,
    hasInvalidInternalRoleDomain,
    hasRole,
    hasPortalReadAccess,
    hasPlatformSwitchAccess,
    hasReadAccess: hasPortalReadAccess,
    hasPanFinderAccess,
    hasWebhookAccess,
    allowedActions: {
      cardLoadAndUnload: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Balance Manager",
      ]),
      editCardConfigurations: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Configuration Manager",
      ]),
      changeCardStatus: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Operations Specialist",
      ]),
      activateCard: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Operations Specialist",
      ]),
      extendThreddExpiryDate: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Operations Specialist",
      ]),
      EditCardHolderDetails: hasRole([...INTERNAL_ROLES, ...FULL_ACCESS_ROLES]),
      removeAuth: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Operations Specialist",
      ]),
      balanceTransfer: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Balance Manager",
      ]),
      balanceAdjustment: hasRole([
        ...INTERNAL_ROLES,
        ...FULL_ACCESS_ROLES,
        "Card Balance Manager",
      ]),
      pinCvvServices: hasRole([
        "Internal",
        ...FULL_ACCESS_ROLES,
        "Card Operations Specialist",
      ]),
    },
  };
};

export const usePortalRoles = import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE
  ? useMockedPortalRoles
  : useOIDCPortalRoles;
